import React, { useState, useEffect } from 'react';
import { FiX, FiDownload, FiFileText } from 'react-icons/fi';
import { useStateContext } from '../../contexts/ContextProvider';
import { userRequest } from '../../utils/requestMethods';
import { generateSchoolSessions } from '../../utils/schoolYearUtils';
import * as XLSX from 'xlsx';
import { toast } from 'react-toastify';

const BroadsheetModal = ({ isOpen, onClose }) => {
  const { school } = useStateContext();
  const schoolTerm = school?.settings?.current_term;
  const schoolSession = school?.settings?.current_session;

  // Modal state
  const [isCumulative, setIsCumulative] = useState(false);
  const [selectedClass, setSelectedClass] = useState(() => {
    const saved = JSON.parse(localStorage.getItem('selectedClass'));
    return saved || { id: null, name: 'Select Class' };
  });
  const [selectedTerm, setSelectedTerm] = useState(() => {
    const saved = JSON.parse(localStorage.getItem('selectedTerm'));
    return saved || { id: schoolTerm || '1', name: `Term ${schoolTerm || '1'}` };
  });
  const [selectedSession, setSelectedSession] = useState(() => {
    const saved = JSON.parse(localStorage.getItem('selectedSession'));
    return saved || { id: schoolSession || '2024/2025', name: schoolSession || '2024/2025' };
  });

  // Dropdown states
  const [classDropdown, setClassDropdown] = useState(false);
  const [termDropdown, setTermDropdown] = useState(false);
  const [sessionDropdown, setSessionDropdown] = useState(false);
  const [exporting, setExporting] = useState(false);

  // Classes list
  const [classes, setClasses] = useState([]);
  const [loadingClasses, setLoadingClasses] = useState(false);

  // Generate school sessions
  const schoolSessions = generateSchoolSessions(2020, 10);

  // Fetch classes
  useEffect(() => {
    const fetchClasses = async () => {
      if (school) {
        try {
          setLoadingClasses(true);
          const { data } = await userRequest.get(`classes?code=${school?.code}`);
          setClasses(data.classes || []);
        } catch (error) {
          console.error("Error fetching classes:", error);
        } finally {
          setLoadingClasses(false);
        }
      }
    };
    fetchClasses();
  }, [school]);

  // Initialize term/session from school settings if not in localStorage
  useEffect(() => {
    if (school?.settings?.current_term && !localStorage.getItem('selectedTerm')) {
      setSelectedTerm({ id: school.settings.current_term, name: `Term ${school.settings.current_term}` });
    }
    if (school?.settings?.current_session && !localStorage.getItem('selectedSession')) {
      setSelectedSession({ id: school.settings.current_session, name: school.settings.current_session });
    }
  }, [school?.settings?.current_term, school?.settings?.current_session]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (classDropdown || termDropdown || sessionDropdown) {
        if (!event.target.closest('.broadsheet-modal')) {
          setClassDropdown(false);
          setTermDropdown(false);
          setSessionDropdown(false);
        }
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [classDropdown, termDropdown, sessionDropdown]);

  // Fetch all results for a class (handle pagination)
  const fetchAllClassResults = async (classId, term, session) => {
    try {
      let allResults = [];
      let page = 1;
      let hasMore = true;

      while (hasMore) {
        const { data } = await userRequest.get(
          `result/${school?._id}?class_id=${classId}&term=${term}&session=${session}&limit=100&page=${page}`
        );
        
        if (data?.data && Array.isArray(data.data)) {
          allResults = [...allResults, ...data.data];
          hasMore = data.data.length === 100 && page < (data.total_pages || 1);
          page++;
        } else {
          hasMore = false;
        }
      }

      return allResults;
    } catch (error) {
      console.error('Error fetching all class results:', error);
      toast.error('Failed to fetch results');
      return [];
    }
  };

  // Helper function to check if a student is deleted
  const isDeletedStudent = (student) => {
    const registrationId = student.child?.registration_id || '';
    const firstname = student.child?.firstname || '';
    const lastname = student.child?.lastname || '';
    
    return registrationId.includes('(Deleted)') || 
           firstname.includes('(Deleted)') || 
           lastname.includes('(Deleted)');
  };

  // Fetch cumulative results for all terms in a session
  const fetchCumulativeResults = async (classId, session) => {
    try {
      const allTermsResults = {};
      
      // Fetch results for all 3 terms
      for (let term = 1; term <= 3; term++) {
        const termResults = await fetchAllClassResults(classId, term, session);
        // Filter out deleted students
        allTermsResults[term] = termResults.filter(student => !isDeletedStudent(student));
      }

      return allTermsResults;
    } catch (error) {
      console.error('Error fetching cumulative results:', error);
      toast.error('Failed to fetch cumulative results');
      return {};
    }
  };

  // Export single term broadsheet
  const exportTermBroadsheet = async () => {
    if (!selectedClass.id) {
      toast.error('Please select a class');
      return;
    }

    setExporting(true);
    try {
      const allResults = await fetchAllClassResults(
        selectedClass.id,
        selectedTerm.id,
        selectedSession.id
      );

      // Filter out deleted students
      const results = allResults.filter(student => !isDeletedStudent(student));

      if (results.length === 0) {
        toast.error('No results found for the selected criteria');
        setExporting(false);
        return;
      }

      // Get all unique subjects from results
      const subjectMap = new Map();
      results.forEach(student => {
        if (Array.isArray(student.results)) {
          student.results.forEach(subjectResult => {
            const subjectId = subjectResult.subject?.id || subjectResult.subject?._id;
            if (subjectId && !subjectMap.has(subjectId)) {
              subjectMap.set(subjectId, {
                id: subjectId,
                name: subjectResult.subject?.name || 'Unknown Subject'
              });
            }
          });
        }
      });

      const subjects = Array.from(subjectMap.values());
      const caColumns = Array.isArray(school?.settings?.assessment?.ca?.cas)
        ? school.settings.assessment.ca.cas
        : [];

      // Calculate total columns per subject: individual CAs + CA Total + Exam + Total
      const columnsPerSubject = caColumns.length + 3; // CAs + CA Total + Exam + Total

      // Build header rows - two rows for merged headers
      // Row 1: Empty cell for names, then subject names (will be merged across all their columns), then Overall columns
      const headerRow1 = [''];
      // Row 2: NAMES, then individual CA columns, CA Total, Exam, Total for each subject, then Overall Total, Overall Percentage
      const headerRow2 = ['NAMES'];
      
      subjects.forEach(subject => {
        // Add placeholders for merge in row 1
        for (let i = 0; i < columnsPerSubject; i++) {
          if (i === 0) {
            headerRow1.push(subject.name.toUpperCase());
          } else {
            headerRow1.push(''); // Placeholder for merge
          }
        }
        
        // Add individual CA columns
        caColumns.forEach((ca, idx) => {
          headerRow2.push(ca.title || `CA ${idx + 1}`);
        });
        // Add CA Total, Exam, Total
        headerRow2.push('CA Total');
        headerRow2.push('Exam');
        headerRow2.push('Total');
      });
      
      // Add Overall columns
      headerRow1.push('OVERALL');
      headerRow1.push(''); // Placeholder for merge
      headerRow2.push('Overall Total');
      headerRow2.push('Overall Percentage');

      // Build data rows
      const rows = results.map(student => {
        const row = [student.child?.firstname && student.child?.lastname 
          ? `${student.child.firstname} ${student.child.lastname}` 
          : student.child?.firstname || student.child?.lastname || 'Unknown'];
        
        let overallTotal = 0;
        let totalPossible = 0;

        subjects.forEach(subject => {
          const subjectResult = student.results?.find(
            r => (r.subject?.id || r.subject?._id) === subject.id
          );

          if (subjectResult) {
            // Add individual CA scores
            caColumns.forEach((ca, idx) => {
              const caObj = Array.isArray(subjectResult.cas) 
                ? subjectResult.cas.find(c => c.ca === idx + 1)
                : null;
              row.push(caObj?.score ?? 0);
            });
            
            // Calculate CA total (sum of all CA scores)
            const caTotal = Array.isArray(subjectResult.cas)
              ? subjectResult.cas.reduce((sum, ca) => sum + (ca.score || 0), 0)
              : 0;
            
            const exam = subjectResult.exam ?? 0;
            const total = subjectResult.total ?? 0;
            
            row.push(caTotal); // CA Total
            row.push(exam); // Exam
            row.push(total); // Total
            
            // Accumulate for overall total
            overallTotal += total;
            totalPossible += 100; // Each subject is out of 100
          } else {
            // No result for this subject - add zeros for all columns
            caColumns.forEach(() => row.push(0)); // Individual CAs
            row.push(0); // CA Total
            row.push(0); // Exam
            row.push(0); // Total
            totalPossible += 100; // Still count as possible
          }
        });

        // Calculate overall percentage
        const overallPercentage = totalPossible > 0 
          ? ((overallTotal / totalPossible) * 100).toFixed(2) 
          : 0;
        
        row.push(overallTotal); // Overall Total
        row.push(`${overallPercentage}%`); // Overall Percentage

        return row;
      });

      // Create worksheet with two header rows
      const worksheetData = [headerRow1, headerRow2, ...rows];
      const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);

      // Merge cells for subject headers (row 1, spanning all columns per subject)
      let colIndex = 1; // Start after NAMES column
      subjects.forEach(() => {
        if (!worksheet['!merges']) worksheet['!merges'] = [];
        worksheet['!merges'].push({ 
          s: { r: 0, c: colIndex }, 
          e: { r: 0, c: colIndex + columnsPerSubject - 1 } 
        });
        colIndex += columnsPerSubject;
      });

      // Merge cells for Overall header (row 1, spanning 2 columns)
      const overallStartCol = colIndex;
      if (!worksheet['!merges']) worksheet['!merges'] = [];
      worksheet['!merges'].push({ 
        s: { r: 0, c: overallStartCol }, 
        e: { r: 0, c: overallStartCol + 1 } 
      });

      // Set frozen panes (freeze column 1 and rows 0-1)
      if (!worksheet['!views']) worksheet['!views'] = [];
      worksheet['!views'].push({
        state: 'frozen',
        xSplit: 1,
        ySplit: 2,
        topLeftCell: 'B3',
        activePane: 'bottomRight'
      });

      // Set column widths
      const colWidths = [{ wch: 25 }]; // Names column
      subjects.forEach(() => {
        caColumns.forEach(() => colWidths.push({ wch: 10 })); // Individual CA columns
        colWidths.push({ wch: 10 }); // CA Total
        colWidths.push({ wch: 10 }); // Exam
        colWidths.push({ wch: 10 }); // Total
      });
      colWidths.push({ wch: 15 }); // Overall Total
      colWidths.push({ wch: 18 }); // Overall Percentage
      worksheet['!cols'] = colWidths;

      // Create workbook
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(
        workbook,
        worksheet,
        `Term ${selectedTerm.id}`
      );

      // Generate filename
      const className = selectedClass.name.replace(/\s+/g, '_');
      const filename = `${className}_Term_${selectedTerm.id}_${selectedSession.id.replace(/\//g, '_')}.xlsx`;

      // Download
      XLSX.writeFile(workbook, filename);
      toast.success('Broadsheet downloaded successfully!');
      onClose();
    } catch (error) {
      console.error('Error exporting broadsheet:', error);
      toast.error('Failed to export broadsheet');
    } finally {
      setExporting(false);
    }
  };

  // Export cumulative session broadsheet
  const exportCumulativeBroadsheet = async () => {
    if (!selectedClass.id) {
      toast.error('Please select a class');
      return;
    }

    setExporting(true);
    try {
      const allTermsResults = await fetchCumulativeResults(
        selectedClass.id,
        selectedSession.id
      );

      // Check if we have any results
      const hasResults = Object.values(allTermsResults).some(termResults => termResults.length > 0);
      if (!hasResults) {
        toast.error('No results found for the selected session');
        setExporting(false);
        return;
      }

      // Get all unique subjects from all terms
      const subjectMap = new Map();
      Object.values(allTermsResults).forEach(termResults => {
        termResults.forEach(student => {
          if (Array.isArray(student.results)) {
            student.results.forEach(subjectResult => {
              const subjectId = subjectResult.subject?.id || subjectResult.subject?._id;
              if (subjectId && !subjectMap.has(subjectId)) {
                subjectMap.set(subjectId, {
                  id: subjectId,
                  name: subjectResult.subject?.name || 'Unknown Subject'
                });
              }
            });
          }
        });
      });

      const subjects = Array.from(subjectMap.values());

      // Get all unique students from all terms
      const studentMap = new Map();
      Object.values(allTermsResults).forEach(termResults => {
        termResults.forEach(student => {
          const studentId = student.child?._id;
          if (studentId && !studentMap.has(studentId)) {
            studentMap.set(studentId, {
              id: studentId,
              name: student.child?.firstname && student.child?.lastname
                ? `${student.child.firstname} ${student.child.lastname}`
                : student.child?.firstname || student.child?.lastname || 'Unknown'
            });
          }
        });
      });

      const students = Array.from(studentMap.values());

      // Build header rows - two rows for merged headers
      // Row 1: Empty cell for names, then subject names (will be merged across 4 columns), then Overall columns
      const headerRow1 = [''];
      // Row 2: NAMES, then Term 1, Term 2, Term 3, Session Total (300) for each subject, then Overall Total, Overall Percentage
      const headerRow2 = ['NAMES'];
      
      subjects.forEach(subject => {
        headerRow1.push(subject.name.toUpperCase());
        headerRow1.push(''); // Placeholder for merge
        headerRow1.push(''); // Placeholder for merge
        headerRow1.push(''); // Placeholder for merge
        
        headerRow2.push('Term 1');
        headerRow2.push('Term 2');
        headerRow2.push('Term 3');
        headerRow2.push('Session Total (300)');
      });
      
      // Add Overall columns
      headerRow1.push('OVERALL');
      headerRow1.push(''); // Placeholder for merge
      headerRow2.push('Overall Total');
      headerRow2.push('Overall Percentage');

      // Build data rows
      const rows = students.map(student => {
        const row = [student.name];
        
        let overallTotal = 0;
        let totalPossible = 0;

        subjects.forEach(subject => {
          const term1Result = allTermsResults[1]?.find(
            s => s.child?._id === student.id
          )?.results?.find(r => (r.subject?.id || r.subject?._id) === subject.id);
          const term2Result = allTermsResults[2]?.find(
            s => s.child?._id === student.id
          )?.results?.find(r => (r.subject?.id || r.subject?._id) === subject.id);
          const term3Result = allTermsResults[3]?.find(
            s => s.child?._id === student.id
          )?.results?.find(r => (r.subject?.id || r.subject?._id) === subject.id);

          const term1Total = term1Result?.total ?? 0;
          const term2Total = term2Result?.total ?? 0;
          const term3Total = term3Result?.total ?? 0;
          const sessionTotal = term1Total + term2Total + term3Total;

          row.push(term1Total);
          row.push(term2Total);
          row.push(term3Total);
          row.push(sessionTotal);

          overallTotal += sessionTotal;
          totalPossible += 300; // 100 per term * 3 terms
        });

        const percentage = totalPossible > 0 ? ((overallTotal / totalPossible) * 100).toFixed(2) : 0;
        row.push(overallTotal);
        row.push(`${percentage}%`);

        return row;
      });

      // Create worksheet with two header rows
      const worksheetData = [headerRow1, headerRow2, ...rows];
      const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);

      // Merge cells for subject headers (row 1, spanning 4 columns each)
      let colIndex = 1; // Start after NAMES column
      subjects.forEach(() => {
        if (!worksheet['!merges']) worksheet['!merges'] = [];
        worksheet['!merges'].push({ 
          s: { r: 0, c: colIndex }, 
          e: { r: 0, c: colIndex + 3 } 
        });
        colIndex += 4;
      });

      // Merge cells for Overall header (row 1, spanning 2 columns)
      const overallStartCol = colIndex;
      if (!worksheet['!merges']) worksheet['!merges'] = [];
      worksheet['!merges'].push({ 
        s: { r: 0, c: overallStartCol }, 
        e: { r: 0, c: overallStartCol + 1 } 
      });

      // Set frozen panes (freeze column 1 and rows 0-1)
      if (!worksheet['!views']) worksheet['!views'] = [];
      worksheet['!views'].push({
        state: 'frozen',
        xSplit: 1,
        ySplit: 2,
        topLeftCell: 'B3',
        activePane: 'bottomRight'
      });

      // Set column widths
      const colWidths = [{ wch: 25 }]; // Names column
      subjects.forEach(() => {
        colWidths.push({ wch: 12 }); // Term 1
        colWidths.push({ wch: 12 }); // Term 2
        colWidths.push({ wch: 12 }); // Term 3
        colWidths.push({ wch: 18 }); // Session Total
      });
      colWidths.push({ wch: 15 }); // Overall Total
      colWidths.push({ wch: 18 }); // Overall Percentage
      worksheet['!cols'] = colWidths;

      // Create workbook
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(
        workbook,
        worksheet,
        'Cumulative'
      );

      // Generate filename
      const className = selectedClass.name.replace(/\s+/g, '_');
      const filename = `${className}_Cumulative_${selectedSession.id.replace(/\//g, '_')}.xlsx`;

      // Download
      XLSX.writeFile(workbook, filename);
      toast.success('Cumulative broadsheet downloaded successfully!');
      onClose();
    } catch (error) {
      console.error('Error exporting cumulative broadsheet:', error);
      toast.error('Failed to export cumulative broadsheet');
    } finally {
      setExporting(false);
    }
  };

  const handleDownload = () => {
    if (isCumulative) {
      exportCumulativeBroadsheet();
    } else {
      exportTermBroadsheet();
    }
  };

  const handleClassSelection = (id, name) => {
    setSelectedClass({ id, name });
    setClassDropdown(false);
  };

  const handleTermSelection = (id, name) => {
    setSelectedTerm({ id, name });
    setTermDropdown(false);
  };

  const handleSessionSelection = (id, name) => {
    setSelectedSession({ id, name });
    setSessionDropdown(false);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl overflow-hidden broadsheet-modal my-auto max-h-[95vh] flex flex-col">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-gray-200 bg-gradient-to-r from-indigo-50 to-purple-50 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center">
              <FiFileText className="w-5 h-5 text-indigo-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Download Broadsheet</h2>
              <p className="text-sm text-gray-500">Select class, term and session for export</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            disabled={exporting}
          >
            <FiX className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content - Scrollable */}
        <div className="p-6 space-y-4 overflow-y-auto flex-1 min-h-0">
          {/* Class Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Class <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <button
                type="button"
                onClick={() => setClassDropdown(!classDropdown)}
                disabled={exporting || loadingClasses}
                className="w-full text-sm font-semibold flex items-center justify-between bg-white hover:bg-indigo-50 border-2 border-indigo-200 hover:border-indigo-300 py-2.5 px-4 rounded-xl transition-all duration-200 disabled:opacity-50"
              >
                <span className={selectedClass.id ? '' : 'text-gray-400'}>
                  {loadingClasses ? 'Loading...' : selectedClass.name}
                </span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className={`ml-2 flex-shrink-0 transition-transform ${classDropdown ? 'rotate-180' : ''}`}
                >
                  <path d="M6 9l6 6l6 -6" />
                </svg>
              </button>
              {classDropdown && (
                <div className="absolute left-0 right-0 bg-white border-2 border-indigo-200 rounded-xl shadow-xl mt-2 z-20 max-h-60 overflow-y-auto">
                  {classes.map(classe => (
                    <div
                      key={classe._id}
                      onClick={() => handleClassSelection(classe._id, classe.class_name)}
                      className="cursor-pointer text-sm px-4 py-2 hover:bg-indigo-50 hover:font-semibold"
                    >
                      {classe.class_name}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Cumulative Toggle */}
          <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl border-2 border-gray-200">
            <input
              type="checkbox"
              id="cumulative"
              checked={isCumulative}
              onChange={(e) => setIsCumulative(e.target.checked)}
              disabled={exporting}
              className="w-5 h-5 text-indigo-600 rounded focus:ring-indigo-500"
            />
            <label htmlFor="cumulative" className="flex-1 cursor-pointer">
              <div className="font-medium text-gray-900">Cumulative (Entire Session)</div>
              <div className="text-sm text-gray-500">
                Download cumulative results for all terms in the session
              </div>
            </label>
          </div>

          {!isCumulative && (
            <>
              {/* Term Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Term
                </label>
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setTermDropdown(!termDropdown)}
                    disabled={exporting}
                    className="w-full text-sm font-semibold flex items-center justify-between bg-white hover:bg-indigo-50 border-2 border-indigo-200 hover:border-indigo-300 py-2.5 px-4 rounded-xl transition-all duration-200"
                  >
                    <span>{selectedTerm.name}</span>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className={`ml-2 transition-transform ${termDropdown ? 'rotate-180' : ''}`}
                    >
                      <path d="M6 9l6 6l6 -6" />
                    </svg>
                  </button>
                  {termDropdown && (
                    <div className="absolute left-0 right-0 bg-white border-2 border-indigo-200 rounded-xl shadow-xl mt-2 z-20 max-h-60 overflow-y-auto">
                      {[1, 2, 3].map(termNum => (
                        <div
                          key={termNum}
                          onClick={() => handleTermSelection(termNum.toString(), `Term ${termNum}`)}
                          className="cursor-pointer text-sm px-4 py-2 hover:bg-indigo-50 hover:font-semibold"
                        >
                          Term {termNum}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </>
          )}

          {/* Session Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Session
            </label>
            <div className="relative">
              <button
                type="button"
                onClick={() => setSessionDropdown(!sessionDropdown)}
                disabled={exporting}
                className="w-full text-sm font-semibold flex items-center justify-between bg-white hover:bg-indigo-50 border-2 border-indigo-200 hover:border-indigo-300 py-2.5 px-4 rounded-xl transition-all duration-200"
              >
                <span className="truncate">{selectedSession.name}</span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className={`ml-2 flex-shrink-0 transition-transform ${sessionDropdown ? 'rotate-180' : ''}`}
                >
                  <path d="M6 9l6 6l6 -6" />
                </svg>
              </button>
              {sessionDropdown && (
                <div className="absolute left-0 right-0 bg-white border-2 border-indigo-200 rounded-xl shadow-xl mt-2 z-20 max-h-60 overflow-y-auto">
                  {schoolSessions.map(session => (
                    <div
                      key={session.value}
                      onClick={() => handleSessionSelection(session.value, session.label)}
                      className="cursor-pointer text-sm px-4 py-2 hover:bg-indigo-50 hover:font-semibold"
                    >
                      {session.label}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Info */}
          <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-800">
              {isCumulative
                ? 'This will download cumulative results showing totals for each term (100 per term, 300 for session) with overall totals and percentages.'
                : 'This will download a broadsheet with all students, subjects, individual CAs, CA Total, Exam, Total, and Overall Total & Percentage for the selected term.'}
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 p-6 border-t border-gray-200 bg-gray-50 flex-shrink-0">
          <button
            onClick={onClose}
            disabled={exporting}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleDownload}
            disabled={exporting || !selectedClass.id}
            className="px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-indigo-600 to-indigo-700 hover:from-indigo-700 hover:to-indigo-800 rounded-lg transition-all disabled:opacity-50 flex items-center gap-2"
          >
            {exporting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                Exporting...
              </>
            ) : (
              <>
                <FiDownload className="w-4 h-4" />
                Download
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BroadsheetModal;

